import type { Column } from '../../../../claim-history/simple-table'
import type { DeliveryTableData, DeliveryState } from './types'
import type { BaseSyntheticEvent, CSSProperties } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useContext, useEffect, useState, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import Select from 'react-select'

import { DiscardDialogContext } from '../../../../../lib/global-discard-dialog'
import * as Table from '../../../../claim-history/simple-table'
import { PlainButton } from '../../../../shared/Button'
import * as Card from '../../../../shared/card'
import { MaterialDeleteButton } from '../../../../shared/material-delete-button'
import { Button, PortedModal } from '../../../../shared/NarusMaterial'
import { ContextWarning, useWarning } from '../components/delivered-context'
import { Input, Textarea } from '../components/input'

import {
  invalidateDeliveryHistory,
  useDeliveryDeleteMutation,
  useDeliveryHistory,
  useDeliverySubmitMutation,
  useDeliveryUpdateMutation
} from './queries'
import { deliveryHistorySchema } from './types'

const TYPES = [
  { value: 'nvd', label: 'Normal Vaginal Delivery' },
  { value: 'cs', label: 'Cesarean Section' },
  { value: 'abortion', label: 'Abortion' },
  { value: 'other', label: 'Other' }
]
const SEX = [{ value: 'm', label: 'Male' }, { value: 'f', label: 'Female' }, { value: 'unknown', label: 'Unknown' }]

const inputStyles: CSSProperties = {
  visibility: 'visible',
  fontSize: '12px',
  cursor: 'text',
  lineHeight: '14px',
  letterSpacing: '0.5px',
  color: '#6e6c6f'
}

export interface ModalState {
  open: boolean
  title: string
  actions: JSX.Element[]
}

export function DeliveryHistory (): JSX.Element {
  const { warning, hide } = useWarning('delivery-history')
  const [ newForm, setNewForm ] = useState<boolean>(false)
  const params = useParams()

  const deliveryHistory = useDeliveryHistory(params.id)
  const submitMutation = useDeliverySubmitMutation(params?.id)
  let [ modal, setModal ] = useState<ModalState>()
  let [ paging, setPaging ] = useState<{ page: number, pageSize: number }>({ page: 0, pageSize: 10 })
  const deleteMutation = useDeliveryDeleteMutation(params?.id ?? '', {
    onSuccess () {
      invalidateDeliveryHistory(params.id ?? '')
      toast.success('Successfully deleted', { position: 'bottom-center' })
    }
  })

  const updateMutation = useDeliveryUpdateMutation(params?.id)

  const columns = useMemo((): Array<Column<DeliveryTableData>> => [
    {
      name: 'date',
      header: 'Date'
    },

    {
      name: 'type',
      header: 'Type',
      getValue: (x) => {
        return TYPES.find((t) => t?.value === x.type)?.label ?? null
      }
    },
    {
      name: 'age',
      header: 'Gestational Age'
    },
    {
      name: 'sex',
      header: 'Sex',
      getValue: (x) => {
        return SEX.find((s) => s?.value === x.sex)?.label ?? null
      }
    },
    {
      name: 'details',
      header: 'Details'
    },
    {
      name: 'actions',
      header: null,
      style: {
        flexShrink: 1
      },
      getValue: (entry) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <MaterialDeleteButton
            onClick={() => {
              setModal({
                title: 'Are you sure you want to delete this record?',
                open: true,
                actions: [
                  <Button
                    key={1}
                    data-cy='modal_cancel-btn'
                    label='Cancel'
                    // @ts-expect-error // It's fine
                    onClick={() => {
                      setModal(undefined)
                    }}
                    grey
                  />,
                  <Button
                    key={2}
                    label='Delete'
                    data-cy='modal_discard-btn'
                    // @ts-expect-error // It's fine
                    onClick={() => {
                      deleteMutation.mutate({ id: entry.id })

                      setModal(undefined)
                    }}
                  />
                ]
              })
            }}
          />
        </div>
      )
    }
  ], [ deleteMutation ])

  const showForm = newForm || warning

  return (
    <Card.Card expandable>
      <Card.Header>
        <div className='flex flex-row w-full justify-between'>
          <h2>Delivery History</h2>
          {!showForm && (
            <button
              className='px-6 py-1.5 rounded-full border border-solid border-primary text-primary font-medium cursor-pointer'
              onClick={() => setNewForm(true)}
            >
              Add Record
            </button>
          )}
        </div>
      </Card.Header>
      <Card.ExpandableWrapper>
        {showForm && (
          <Form
            handleCancel={() => {
              setNewForm(false)
              hide()
            }}
            handleSubmit={(value) => {
              setNewForm(false)
              hide()
              submitMutation.mutate(value)
            }}
          />
        )}

        <Table.Table
          expandable
          expandingComponent={(item, _, setState) => (
            <Form
              initialState={item}
              handleSubmit={(values) => {
                updateMutation.mutate({ id: item.id, data: values })
                setState(null)
              }}
              handleCancel={() => setState(null)}
            />
          )}
          noDataPlaceholder='There are no records yet'
          columns={columns}
          data={deliveryHistory.data?.slice((paging.page) * paging.pageSize, paging.page * paging.pageSize + paging.pageSize) ?? []}
          error={deliveryHistory.error as Error}
          queryStatus={deliveryHistory.status}
          fetchStatus={deliveryHistory.fetchStatus}
          pagination={{ ...paging, totalFiltered: deliveryHistory.data?.length ?? 0 }}
          onPageChange={(page) => setPaging((prev) => ({ ...prev, page: page - 1 }))}
          onPageSizeChange={(pageSize) => setPaging((prev) => ({ ...prev, pageSize }))}
          disableMinHeight
        />
      </Card.ExpandableWrapper>
      <PortedModal
        open={modal?.open}
        title={modal?.title}
        actions={modal?.actions}
      />
    </Card.Card>
  )
}

interface FormProps {
  initialState?: DeliveryState
  handleSubmit: (value: DeliveryState) => void
  handleCancel: () => void
}

function Form ({ initialState, handleSubmit, handleCancel }: FormProps): JSX.Element {
  let discardDialog = useContext(DiscardDialogContext)
  const { hide } = useWarning('delivery-history')
  let { onFormChange, resetForm } = discardDialog ?? {}
  // @ts-expect-error // it's okay
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  let formKey = `admin/users/${initialState?.id ?? 'new'}`
  const isNewForm = !initialState
  const { register, handleSubmit: handleSubmitForm, control, formState } = useForm<DeliveryState>({
    defaultValues: !isNewForm
      ? initialState
      : undefined,
    mode: 'onTouched',
    resolver: zodResolver(deliveryHistorySchema)
  })

  function onValid (data: DeliveryState): void {
    handleSubmit(data)
  }

  function onSubmit (event: BaseSyntheticEvent): void {
    hide()
    handleSubmitForm(
      onValid,
      (err) => console.error('Validation Error', err)
    )(event)
      .catch((e) => console.error(e))
  }

  function onCancel (): void {
    if (discardDialog?.isFormDirty(formKey) ?? false) {
      discardDialog?.show(formKey, () => {
        handleCancel?.()
      })
      return
    }

    handleCancel?.()
  }

  useEffect(() => {
    if (formState.isDirty) {
      onFormChange?.(formKey)
    }

    if (!formState.isDirty) {
      resetForm?.(formKey)
    }
  }, [
    formKey,
    formState.isDirty,
    onFormChange,
    resetForm
  ])

  useEffect(() => {
    return () => {
      resetForm?.(formKey)
    }
  }, [
    formKey,
    resetForm
  ])

  return (
    <form
      onSubmit={onSubmit}
      className='relative z-10'
      id='delivery-history-form'
    >
      <div className='px-8 py-2'>
        <ContextWarning
          contextKey='delivery-history'
          className='pt-2'
        />
      </div>
      <div className='grid grid-cols-[1fr_1fr_2fr] grid-rows-2 grid-flow-row gap-10 py-6 px-8'>
        <Input
          register={register('date')}
          label='Date'
          type='text'
          required
          placeholder='Date'
        />
        <Controller
          control={control}
          render={({ field }) => (
            <div className='flex flex-col gap-1'>
              <label
                style={inputStyles}
                className='relative w-fit'
                htmlFor='type-select'
              >
                Type
                <span
                  style={{
                    position: 'absolute',
                    top: '-3px',
                    right: '-7px',
                    fontSize: '1rem',
                    color: 'red'
                  }}
                >
                  *
                </span>
              </label>

              <Select
                inputId='type-select'
                styles={{
                  valueContainer: (styles: any) => ({
                    ...styles,
                    paddingTop: 8,
                    paddingBottom: 8
                  }),
                  control: (styles: any) => ({
                    ...styles,
                    borderColor: '#DADCE0',
                    zIndex: 10
                  })
                }}
                id='types-select'
                options={TYPES}
                isClearable
                onChange={(n) => field.onChange(n?.value ?? null)}
                value={TYPES.find(t => t.value === field.value) ?? null}
                placeholder='Type'

              />

            </div>
          )}
          name='type'
        />

        <div className='col-start-1 row-start-2'>
          <Input
            register={register('age')}
            type='text'
            label='Gestational Age'
            placeholder='Gestational Age'
          />
        </div>
        <Controller
          control={control}
          render={({ field }) => (
            <div className='flex flex-col gap-1 col-start-2 row-start-2'>
              <label
                htmlFor='gender-select'
                style={ inputStyles }
                className='relative w-fit'
              >
                Sex
              </label>
              <Select
                id='gender-select'
                styles={{
                  valueContainer: (styles: any) => ({
                    ...styles,
                    paddingTop: 8,
                    paddingBottom: 8
                  }),
                  control: (styles: any) => ({
                    ...styles,
                    borderColor: '#DADCE0'
                  })
                }}
                options={SEX}
                isClearable
                onChange={(n) => field.onChange(n?.value)}
                value={SEX.find(t => t.value === field.value) ?? null}
                placeholder='Sex'
              />
            </div>
          )}
          name='sex'
        />
        <div className='row-span-2 col-start-3 row-start-1'>
          <Textarea
            register={register('details')}
            type='text'
            label='Details'
            placeholder='Details'
          />
        </div>
      </div>
      <div className='py-6 px-8 flex flex-row gap-2 justify-end'>
        <PlainButton
          label='Cancel'
          type='button'
          onClick={onCancel}
          data-cy='publish-btn'
        />
        <PlainButton
          color='primary'
          btnType='contained'
          disabled={!formState.isValid || !formState.isDirty}
          label='Save'
          type='submit'
          data-cy='delivery-history-publish-btn'
        />
      </div>
    </form>
  )
}
